import * as React from "react";
import {WithNamespaces, withNamespaces} from "react-i18next";
import {
  GenericalResponse,
  PayloadArtwork,
  ProposalArtwork,
  ProposalComment, ProposalStatus,
  ResponseArtwork,
  ResponseTypes
} from "src/redux/projects";
import ControlBar, {ControlBarEvents} from "./ControlBar";
import {ImagePanWithPin} from "./ImagePanWithPin";
import commentModal from "./CommentModal";
import commentModalLocked from "./CommentModalLocked";
import fscreen from "fscreen";
import {NoPrint, Print} from "react-easy-print";
import ArtworkSidebar from "./ArtworkSidebar";
import {downloadUrl} from "src/helpers/downloadUrl";
import {Profil} from "src/redux/user";
import {toast} from "react-toastify";
import ShareModal from "./ShareModal";
import {shareProposal} from "src/api/projects";
import AlertProposalRankModal from "./AlertProposalRankModal";
import CommentAlertModal from "./CommentAlertModal";
import {config} from "../config/config";

interface Props {
  response: ResponseArtwork | undefined;
  proposal: ProposalArtwork;
  loader: boolean;
  isLocked: boolean;
  profil: Profil;
  handleSubmitCommentForArtwork: (id: string, comment: ProposalComment) => void;
  removeArtwork: (proposalId: string, commentId: string) => void;
  onSubmit: (
    payload: PayloadArtwork,
    response: GenericalResponse | undefined,
    responseType: ResponseTypes,
    share?: boolean,
    newDevis?: boolean
  ) => void;
}
interface State {
  comments: ProposalComment[];
  // Whether the user wants to pin a comment on artwork
  wantsToPin: boolean;

  // image zoom
  imageZoom: number;
  step: number;
  pinCount: number;
}

type PropsWithNamespaces = Props & WithNamespaces;
class ArtworkPage extends React.Component<PropsWithNamespaces, State> {
  fullscreenDiv = React.createRef<any>();
  state: State = {
    comments: [],
    wantsToPin: false,
    imageZoom: 1,
    step: 0,
    pinCount: 0
  };

  onChangeStep = (value: number) => {
    this.setState({
      step: value
    });
  };
  shareProposalWithModal = async () => {
    const response = await ShareModal({
      proposalType: this.props.proposal.type
    });
    if (!response) {
      return;
    }
    const mail = {
      to: response.emailAdresses.join(","),
      msg: response.text
    };
    await this.props.onSubmit(
      (this.props.response as ResponseArtwork).payload,
      this.props.response,
      ResponseTypes.Pending,
      true
    );
    const responseShare = await shareProposal(mail, this.props.proposal.uuid);
    if (!responseShare) {
      return toast.error(this.props.t("proposal.errorShare"));
    }
    return toast.success(this.props.t("proposal.validShare"));
  };
  controlBarHandler = async (type: ControlBarEvents, data: any) => {
    switch (type) {
      case ControlBarEvents.ZoomIn:
        this.setState({ imageZoom: Math.max(this.state.imageZoom + 0.2, 0) });
        break;
      case ControlBarEvents.ZoomOut:
        this.setState({ imageZoom: Math.max(this.state.imageZoom - 0.2, 0) });
        break;
      case ControlBarEvents.Share:
        this.shareProposalWithModal();
        break;
      case ControlBarEvents.ToggleFullScreen:
        this.setState({ imageZoom: 1 }, () => this.toggleFullScreen());
        break;
      case ControlBarEvents.Print:
        window.print();
        break;
      case ControlBarEvents.Download:
        downloadUrl(
          this.props.proposal.ressources[0].image,
          this.props.proposal.ressources[0].label
        );
        break;
    }
  };

  /**
   * Toggle full screen for image element
   */
  toggleFullScreen() {
    if (fscreen.fullscreenElement === null) {
      fscreen.requestFullscreen(this.fullscreenDiv.current);
    } else {
      fscreen.exitFullscreen();
    }
  }

  /**
   * Pin a new comments on image
   */
  handlePinDrop = async (x: number, y: number, commentId: string) => {
    // A pin with an id of "0" has been dropped: it's a comment creation
    // Otherwise it's a move of an already existing comment
    if (commentId === "0") {
      this.commentCreation(x, y);
    } else {
      this.commentCoordinatesUpdate(x, y, commentId);
    }
  };

  /**
   * Open a way to read / edit a comment
   * If a comment is editable (ie in currentResponse), we open a modal
   * If a comment is readonly (ie in proposal), we open a tooltip
   */
  handlePinClick = async (commentId: string) => {
    const editableComment = this.findCommentInCurrentResponse(commentId);
    if (editableComment) {
      return this.editCommentWithModal(editableComment);
    }

    const readonlyComment = this.findCommentInProposal(commentId);
    if (readonlyComment) {
      return this.readCommentWithModal(readonlyComment);
    }
    console.error("Can not edit comment: comment not found in store");
  };
  /**
   * Read only a comment using a modal
   */
  readCommentWithModal = (comment: ProposalComment) => {
    commentModalLocked({
      comment
    });
  };

  /**
   * Edit a comment using a modal
   */
  editCommentWithModal = async (comment: ProposalComment) => {
    const allComments = this.getAllComments();
    const response = await commentModal({
      comment,
      showWarning: allComments.length > config.maxCommentCount
    });
    if (!response) {
      return;
    }
    /**
     * Remove comment
     */
    if (response.remove) {
      this.props.removeArtwork(this.props.proposal.uuid, comment.id);
      return;
    }
    if (!response.text) {
      return;
    }
    comment.files = response.files;
    comment.text = response.text;
    this.props.handleSubmitCommentForArtwork(comment.id, comment);
  };

  /**
   * Create a new comment: add a new popin, open a modal to enter comment, ...
   */
  commentCreation = async (x: number, y: number) => {
    let allComments = this.getAllComments();
    if(allComments.length>=config.maxCommentCount){
      await CommentAlertModal({
        title:'Plus de commentaire disponible',
        message:'Vous avez déjà mis plus de '+config.maxCommentCount+' commentaires sur cette image.'
      });
      return;
    }
    // 1. add a temporary comment in state, so we can display it's pin
    const newComment = {
      id: "0", // Their should never be two comments with an id of 0
      x,
      y,
      text: "",
      files: []
    };

    this.setState({
      comments: [...this.state.comments, newComment]
    });

    // 2. Open a modal to create a comment

    const response = await commentModal({
      comment: newComment,
      showWarning: allComments.length > config.maxCommentCount
    });
    // 3. Modal is saved: keep the comment and store it in redux
    if (response) {
      if (!response.text) {
        // Delete comment if he hasn't text
        const newState = this.state.comments.filter(c => c.id !== "0");
        this.setState({
          comments: newState
        });
        return;
      }
      newComment.text = response.text;
      newComment.files = response.files;
      this.props.handleSubmitCommentForArtwork(newComment.id, newComment);
    }

    // 4. Remove the temp pin now. If it has been saved, comment will be in this.props now
    this.setState({
      comments: [...this.state.comments.filter(c => c.id !== "0")]
    });
    allComments = this.getAllComments();
    if(allComments.length===config.maxCommentCount-5){
      CommentAlertModal({
        title:'Attention ! Plus que 5 commentaires disponibles',
        message:'Il vous reste 5 commentaires disponibles sur les '+config.maxCommentCount+' dont vous bénéficiez.'
      });
    }
    if(allComments.length===config.maxCommentCount){
      CommentAlertModal({
        title:'Plus de commentaire disponible',
        message:'Vous venez de déposer votre dernier commentaire.'
      });
    }
  };

  /**
   * Edit a comment when it's pin has been moved
   */
  commentCoordinatesUpdate = (x: number, y: number, commentId: string) => {
    const comment = this.findCommentInCurrentResponse(commentId);
    if (!comment) {
      console.error("Can not edit comment: comment not found in store");
      return;
    }

    comment.x = x;
    comment.y = y;
    this.props.handleSubmitCommentForArtwork(commentId, comment);
  };

  /**
   * Find a comment in redux current response
   */
  findCommentInCurrentResponse(id: string) {
    // Find comment in current
    if (!this.props.response) {
      return null;
    }

    const commentsFromRedux = this.props.response.payload.comments;

    return commentsFromRedux.find(c => c.id === id);
  }

  /**
   * Find comment in current proposal
   */
  findCommentInProposal(id: string) {
    const comments = (this.props.proposal as ProposalArtwork).responses.reduce(
      (acc, r) => acc.concat(r.payload.comments),
      [] as ProposalComment[]
    );

    return comments.find(c => c.id === id);
  }
  /**
   * Once image has loaded, we calculate first zoom so the whole image fits in pan div
   */
  onImageLoaded = (image: HTMLImageElement) => {
    const fittingZoom =
      this.fullscreenDiv.current.offsetHeight / image.offsetHeight;
    this.setState({ imageZoom: fittingZoom - 0.25 });
  };
  onSubmit = async (payload: PayloadArtwork,
                    response: GenericalResponse | undefined,
                    responseType: ResponseTypes,
                    share?: boolean)=>{
    const newDevis=(this.props.proposal.rank !== undefined)
                    && (this.props.proposal.rank>1)
                    && responseType===ResponseTypes.Rejected ;
    await this.props.onSubmit(payload,response,responseType,share,newDevis);

  }
  getAllComments = () => {
    // Some comments come from currentResponse object ( the response this user is filling / editing )

    const currentResponseComments = this.props.response
      ? this.props.response.payload.comments
      : [];
    // Some comments come from displayed proposal ( the response saved on api side )
    // We consider only comments made by another user to avoid duplicate
    const castedProposal = this.props.proposal as ProposalArtwork;

    // In locked mode, we show comments from user
    // in unlocked mode, we take only comments coming from other author, to avoid duplicates
    const responses = this.props.isLocked
      ? castedProposal.responses
      : castedProposal.responses.filter(
          r =>
            this.props.response && r.authorId !== this.props.response.authorId
        );

    const proposalComments = responses.reduce(
      (acc, r) => acc.concat(r.payload.comments),
      [] as ProposalComment[]
    );

    // And some other comments come from state (temporary comments)
    const allComments = [
      ...currentResponseComments,
      ...proposalComments,
      ...this.state.comments
    ];
    return allComments;
  };

  render() {
    const allComments = this.getAllComments();
    return (

      <div className={`proposal  ${this.state.step > 0 ? "expanded" : ""}`}>
        {this.props.proposal.status===ProposalStatus.Pending && <AlertProposalRankModal rank={this.props.proposal.rank}  />}
        {/*this.props.proposal.status===ProposalStatus.Pending && <AlertProposalRankModal rank={2}  />*/}

        <div className="d-flex">
          <div className="proposal-screen">
            <div ref={this.fullscreenDiv}>
              <NoPrint force={true}>
                <ImagePanWithPin
                  url={this.props.proposal.ressources[0].image}
                  comments={allComments}
                  onDrop={this.handlePinDrop}
                  onPinclick={this.handlePinClick}
                  locked={this.props.isLocked}
                  zoom={this.state.imageZoom}
                  onImageLoad={this.onImageLoaded}
                  className={
                    this.state.wantsToPin
                      ? "artwork-image cursor-pin"
                      : "artwork-image"
                  }
                />
              </NoPrint>
            </div>
            <div className="flex-1 d-flex align-items-center justify-content-center">
              <ControlBar
                onEvent={this.controlBarHandler}
                locked={this.props.isLocked}
                buttons={[
                  "fscreen",
                  "zoom",
                  "download",
                  "print",
                  "pin",
                  "share"
                ]}
              />
            </div>
          </div>
          <Print single={true} name="artwork-comments" printOnly={true}>
            <div className="w-100 print-artwork">
              {/* Display image with comment only in print view */}
              <ImagePanWithPin
                url={this.props.proposal.ressources[0].image}
                comments={allComments}
                zoom={1}
              />
              {/* Display all comments only in print view */}
              {allComments.map((c, index) => (
                <div key={c.id}>
                  <span>
                    {index + 1}
                    {" : "}
                    {c.text}
                  </span>
                </div>
              ))}
            </div>
          </Print>
          <NoPrint force={true}>
            <ArtworkSidebar
              editCommentWithModal={this.editCommentWithModal}
              readCommentWithModal={this.readCommentWithModal}
              response={this.props.response}
              onSubmit={this.onSubmit}
              allComments={allComments}
              isLocked={this.props.isLocked}
              onChangeStep={this.onChangeStep}
              step={this.state.step}
              loader={this.props.loader}
              proposal={this.props.proposal}
            />
          </NoPrint>
        </div>
      </div>
    );
  }
}
export default withNamespaces()(ArtworkPage);



// WEBPACK FOOTER //
// ./src/components/Artwork.tsx